import { type ComboboxItem, Loader, Select, type SelectProps } from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import { keepPreviousData } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { resourceInitialData, useAPIResource, useAPIResources } from "@/hooks"
import { cityAPI, cityName, getFlagEmoji, prepareQuery } from "@/utils"

function useSearchCities(query: string) {
  const [debouncedSeach] = useDebouncedValue(query, 300)
  const { data: { data = [] } = {} } = useAPIResources(cityAPI, {
    limit: 20,
    placeholderData: keepPreviousData,
    where: debouncedSeach ? { q: debouncedSeach } : undefined,
  })
  return data
}

export function CitySelect({ value, ...props }: Omit<SelectProps, "data">) {
  const {
    t,
    i18n: { language: lang },
  } = useTranslation()
  const { data: city, isLoading } = useAPIResource(cityAPI, {
    enabled: !!value,
    id: value as string,
    initialData: resourceInitialData(cityAPI.url(), value as string),
  })
  const [search, setSearch] = useState("")
  const query = prepareQuery(search)

  const cities = useSearchCities(search)
  const items = cities.map((obj) => ({
    value: obj.id,
    label: `${cityName(obj, lang)} ${getFlagEmoji(obj.country_code)}`,
  }))
  if (city && !cities.find((c) => c.id == city.id))
    items.push({
      value: city.id,
      label: `${cityName(city, lang)} ${getFlagEmoji(city.country_code)}`,
    })

  useEffect(() => {
    if (city) setSearch(`${cityName(city, lang)} ${getFlagEmoji(city.country_code)}`)
  }, [city])

  return (
    <Select
      w="100%"
      clearable
      label={t("City")}
      searchable
      disabled={isLoading}
      limit={20}
      leftSection={isLoading && <Loader size={16} color="gray" />}
      placeholder={t("Select a city")}
      onSearchChange={setSearch}
      searchValue={search}
      filter={({ options }) => {
        return (options as ComboboxItem[]).filter((opt) => {
          if (!search) return true
          const label = opt.label?.toLowerCase()
          return label.includes(query.latin as string) || label.includes(query.alternate as string)
        })
      }}
      {...props}
      value={value}
      data={items}
    />
  )
}
